import { useEffect, useId, useRef, useState } from "react";

import {
  AI_PROVIDERS,
  accountErrorMessage,
  createProviderConnection,
  getProjectAiSettings,
  isActiveProviderConnectionLifecycle,
  isAiProvider,
  isManagedAiUnavailableError,
  listProviderConnections,
  pauseProjectAiSettings,
  saveProjectAiSettings,
  type AiProvider,
  type ProjectAiSettings as ProjectAiSettingsState,
  type ProviderConnection,
} from "../../lib/account-client";
import { providerName } from "./ProviderConnections";

export interface ProjectAiSettingsApi {
  getProjectAiSettings: typeof getProjectAiSettings;
  saveProjectAiSettings: typeof saveProjectAiSettings;
  pauseProjectAiSettings: typeof pauseProjectAiSettings;
  listProviderConnections: typeof listProviderConnections;
  createProviderConnection: typeof createProviderConnection;
}

export function ProjectAiSettings({
  workspaceId,
  projectId,
  api,
}: {
  workspaceId: string;
  projectId: string;
  api: ProjectAiSettingsApi;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const credentialFormRef = useRef<HTMLFormElement>(null);
  const errorId = useId();
  const modelErrorId = useId();
  const [settings, setSettings] = useState<ProjectAiSettingsState | null>(null);
  const [connections, setConnections] = useState<ProviderConnection[] | null>(null);
  const [connectionId, setConnectionId] = useState("");
  const [model, setModel] = useState("");
  const [monthlyRunCap, setMonthlyRunCap] = useState("100");
  const [addingConnection, setAddingConnection] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [saving, setSaving] = useState(false);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void Promise.all([
      api.getProjectAiSettings(workspaceId, projectId),
      api.listProviderConnections(workspaceId),
    ]).then(([nextSettings, connectionResult]) => {
      if (!active) return;
      const nextConnections = connectionResult.connections;
      setSettings(nextSettings);
      setConnections(nextConnections);
      setAvailable(true);
      setLoadError(null);
      const firstReady = nextConnections.find((connection) => connection.lifecycle === "ready");
      const initialConnectionId = nextSettings.connectionId ?? firstReady?.id ?? "";
      const initialConnection = nextConnections.find((connection) => connection.id === initialConnectionId);
      setConnectionId(initialConnectionId);
      setModel(nextSettings.model ?? firstSelectableModel(initialConnection?.compatibleModels));
      setMonthlyRunCap(String(nextSettings.monthlyRunCap ?? 100));
    }).catch((cause: unknown) => {
      if (!active) return;
      setSettings(null);
      setConnections([]);
      if (isManagedAiUnavailableError(cause)) {
        setAvailable(false);
        setLoadError(null);
        return;
      }
      setAvailable(true);
      setLoadError(accountErrorMessage(cause, "Doorway could not load this project's AI settings."));
    });
    return () => { active = false; };
  }, [api, projectId, workspaceId]);

  const selectedConnection = connections?.find((connection) => connection.id === connectionId);
  const compatibleModels = selectedConnection?.compatibleModels ?? [];
  const selectedModel = compatibleModels.find((item) => item.id === model);
  const modelInvalidated = Boolean(model) && (!selectedModel || Boolean(selectedModel.restriction));
  const committedRuns = settings?.usage?.committed ?? 0;
  const parsedCap = Number(monthlyRunCap);
  const capInvalid = !Number.isSafeInteger(parsedCap) || parsedCap < 1 || parsedCap > 1_000_000 || parsedCap < committedRuns;

  const readyConnections = (connections ?? []).filter((connection) => connection.lifecycle === "ready" || (connection.lifecycle === "rotating" && connection.id === settings?.connectionId));

  const open = () => {
    setError(null);
    setAddingConnection(readyConnections.length === 0);
    dialogRef.current?.showModal();
  };

  const close = () => {
    credentialFormRef.current?.reset();
    setError(null);
    setAddingConnection(false);
    dialogRef.current?.close();
    triggerRef.current?.focus();
  };

  const changeConnection = (nextId: string) => {
    const connection = connections?.find((item) => item.id === nextId);
    setConnectionId(nextId);
    setModel(firstSelectableModel(connection?.compatibleModels));
    setError(null);
  };

  const addConnection = async (form: HTMLFormElement) => {
    if (saving) return;
    const data = new FormData(form);
    const provider = data.get("provider");
    const apiKey = data.get("apiKey");
    if (!isAiProvider(provider) || typeof apiKey !== "string") return;
    setSaving(true);
    setError(null);
    setAnnouncement(`Validating ${providerName(provider)} connection.`);
    try {
      const result = await api.createProviderConnection({ workspaceId, provider, apiKey: apiKey.trim() });
      setConnections((current) => [...(current ?? []).filter((item) => item.provider !== result.connection.provider), result.connection]);
      setConnectionId(result.connection.id);
      setModel(firstSelectableModel(result.connection.compatibleModels));
      setAddingConnection(false);
      form.reset();
      setAnnouncement(`${providerName(provider)} is ready. Choose a model and save this project.`);
    } catch (cause) {
      setError(accountErrorMessage(cause, "Doorway could not connect this provider account."));
      const key = form.elements.namedItem("apiKey");
      if (key instanceof HTMLInputElement) {
        key.value = "";
        key.focus();
      }
      setAnnouncement("Provider connection failed. Review the error and try again.");
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!selectedConnection || !model || capInvalid || saving) return;
    setSaving(true);
    setError(null);
    setAnnouncement("Saving project AI settings.");
    try {
      const updated = await api.saveProjectAiSettings(workspaceId, projectId, {
        connectionId: selectedConnection.id,
        model,
        monthlyRunCap: parsedCap,
        policyVersion: settings?.policyVersion ?? 0,
      });
      setSettings(updated);
      dialogRef.current?.close();
      setAnnouncement("Project AI settings saved.");
      triggerRef.current?.focus();
    } catch (cause) {
      setError(accountErrorMessage(cause, "Doorway could not save these AI settings."));
      setAnnouncement("Project AI settings failed to save. Review the error and try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    setAnnouncement("Pausing AI for this project.");
    try {
      const updated = await api.pauseProjectAiSettings(workspaceId, projectId);
      setSettings(updated);
      dialogRef.current?.close();
      setAnnouncement("AI is paused for this project. The site remains online.");
      triggerRef.current?.focus();
    } catch (cause) {
      setError(accountErrorMessage(cause, "Doorway could not pause AI for this project."));
      setAnnouncement("AI could not be paused. Review the error and try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {available !== false ? (
        <button ref={triggerRef} className="text-button project-ai-button" type="button" disabled={settings === null || connections === null} onClick={open}>
          {settings?.configured ? `${providerName(settings.provider)} · ${modelName(settings.model, settings.compatibleModels)}` : "Set up AI"}
        </button>
      ) : null}
      {loadError ? <p className="error-message" role="alert">{loadError}</p> : null}
      {settings?.configured && settings.monthlyRunCap !== undefined && settings.usage ? (
        <div className="project-ai-usage">
          <strong>{settings.usage.committed} of {settings.monthlyRunCap} AI runs used this month</strong>
          <span>{settings.usage.remaining} remaining. Resets {formatUtcDate(settings.usage.periodEnd)} UTC.</span>
          <small>This Doorway cap is separate from provider billing.</small>
        </div>
      ) : null}
      {settings?.paused && <p className="project-ai-paused" role="status">AI is paused. Your site remains online.</p>}
      <p className="visually-hidden" role="status" aria-live="polite">{announcement}</p>

      <dialog
        className="ai-settings-dialog"
        ref={dialogRef}
        aria-labelledby={`project-ai-heading-${projectId}`}
        onCancel={(event) => { if (saving) event.preventDefault(); }}
        onClose={() => {
          credentialFormRef.current?.reset();
          setError(null);
          setAddingConnection(false);
          triggerRef.current?.focus();
        }}
      >
        <div className="project-ai-dialog-content">
          <div className="ai-settings-heading">
            <div><p className="step-label">Project AI</p><h3 id={`project-ai-heading-${projectId}`}>{settings?.configured ? "Update AI settings" : "Set up AI"}</h3></div>
            <button type="button" className="dialog-close" aria-label="Close AI settings" disabled={saving} onClick={close}>×</button>
          </div>
          <p>Choose one reusable provider connection, one compatible model, and a monthly run cap for this project.</p>
          {error && <p className="error-message" id={errorId} role="alert">{error}</p>}

          {addingConnection ? (
            <form ref={credentialFormRef} className="project-ai-connection-form" onSubmit={(event) => { event.preventDefault(); void addConnection(event.currentTarget); }}>
              <div className="project-ai-context-note"><strong>Connect a provider to continue</strong><span>Your model and cap choices stay here while Doorway validates the account.</span></div>
              <label><span>Provider</span><select name="provider" disabled={saving}>{availableProviders(connections).map((provider) => <option value={provider} key={provider}>{providerName(provider)}</option>)}</select></label>
              <label><span>API key</span><input name="apiKey" type="password" required minLength={20} maxLength={1024} autoComplete="new-password" aria-describedby={error ? errorId : undefined} disabled={saving} /></label>
              <p className="provider-billing-note">Your provider bills you directly. Doorway keeps the key out of your project files and never shows it after submission.</p>
              <div className="ai-settings-actions">
                {readyConnections.length > 0 && <button type="button" className="text-button" disabled={saving} onClick={() => setAddingConnection(false)}>Use existing connection</button>}
                <button type="submit" className="primary-button" disabled={saving || availableProviders(connections).length === 0}>{saving ? "Validating..." : "Connect and continue"}</button>
              </div>
            </form>
          ) : (
            <div className="project-ai-policy-form">
              <label>
                <span>Provider connection</span>
                <select value={connectionId} onChange={(event) => changeConnection(event.target.value)} disabled={saving || readyConnections.length === 0}>
                  {readyConnections.map((connection) => <option key={connection.id} value={connection.id}>{providerName(connection.provider)}</option>)}
                </select>
              </label>
              <button type="button" className="text-button project-ai-add-connection" disabled={saving || availableProviders(connections).length === 0} onClick={() => setAddingConnection(true)}>Add another provider</button>
              <label>
                <span>Model</span>
                <select value={modelInvalidated ? "" : model} onChange={(event) => setModel(event.target.value)} disabled={saving || compatibleModels.length === 0} aria-describedby={`project-ai-model-help-${projectId}${modelInvalidated ? ` ${modelErrorId}` : ""}`}>
                  {modelInvalidated && <option value="">Choose a supported model</option>}
                  {compatibleModels.map((item) => <option key={item.id} value={item.id} disabled={Boolean(item.restriction)}>{item.name}{item.restriction ? ` (${item.restriction})` : ""}</option>)}
                </select>
              </label>
              <p id={`project-ai-model-help-${projectId}`} className="project-ai-cap-help">These models were checked against this provider account when its API key was connected or last rotated.</p>
              {compatibleModels.length === 0 && <p className="project-ai-warning" role="status">This connection has no compatible models available. Try another provider connection.</p>}
              {modelInvalidated && <p className="project-ai-warning" id={modelErrorId} role="alert">The previously selected model is no longer available. Choose another model before saving.</p>}
              <label>
                <span>Monthly AI run cap</span>
                <input type="number" min={Math.max(1, committedRuns)} max={1_000_000} step="1" value={monthlyRunCap} onChange={(event) => setMonthlyRunCap(event.target.value)} disabled={saving} aria-describedby={`project-ai-cap-help-${projectId}`} />
              </label>
              <p id={`project-ai-cap-help-${projectId}`} className={capInvalid ? "project-ai-warning" : "project-ai-cap-help"}>
                {committedRuns > 0 ? `${committedRuns} runs are already committed this month. ` : ""}This limits Doorway AI runs only. Manage provider spending from your provider account.
              </p>
              <div className="ai-settings-actions">
                {settings?.configured && <button type="button" className="text-button danger-button" disabled={saving} onClick={() => void remove()}>Pause project AI</button>}
                <button type="button" className="primary-button" disabled={saving || !selectedConnection || !model || modelInvalidated || capInvalid} onClick={() => void save()}>{saving ? "Saving..." : "Save AI settings"}</button>
              </div>
            </div>
          )}
        </div>
      </dialog>
    </>
  );
}

function availableProviders(connections: ProviderConnection[] | null): AiProvider[] {
  const active = new Set((connections ?? []).filter((connection) => isActiveProviderConnectionLifecycle(connection.lifecycle)).map((connection) => connection.provider));
  return AI_PROVIDERS.filter((provider) => !active.has(provider));
}

function modelName(model: string | undefined, models: ProjectAiSettingsState["compatibleModels"]): string {
  if (!model) return "Model unavailable";
  return models?.find((item) => item.id === model)?.name ?? model;
}

function firstSelectableModel(models: ProviderConnection["compatibleModels"] | undefined): string {
  return models?.find((item) => !item.restriction)?.id ?? "";
}

function formatUtcDate(value: string): string {
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" }).format(new Date(value));
}
